import { useCallback } from 'react';
import { Button, Stack, Typography, styled, useTheme } from '@mui/material';

import { BackBooksRowState } from './toRows';

import CheckCircleFilledIcon from 'modules/common/components/Icons/CheckCircleFilledIcon';
import BankLogo from 'modules/HubV2/Bookkeeping/components/Banks2/BankList/Bank/BankLogo';
import { formatMonthRangeShort } from 'modules/common/components/VerifiedStatementUpload/utils/formatMonthList';
import { accountDisplayName } from 'modules/common/components/VerifiedStatementUpload/utils/periods';
import { AccountStatementCoverage, StatementPeriod } from 'services/bookkeeping/queries/useStatementCoverage';

/**
 * Fixed 40px frame for the institution logo.
 *
 * Load-bearing and not redundant with `BankLogo`'s own `width`/`height`: `BankLogo` forwards `sx`
 * only in its fallback-icon branch, so the mapped-SVG and image branches need sizing from outside.
 */
const LogoFrame = styled(Stack)({
    width: 40,
    height: 40,
    flexShrink: 0,
    '& img, & svg': { width: '100%', height: '100%', objectFit: 'contain' },
});

interface BuildingBackBooksAccountRowProps {
    readonly account: AccountStatementCoverage;
    /** What this account's row says. Decided by `toRows`, never re-derived here. */
    readonly state: BackBooksRowState;
    /** The months this task asks for. Decided by `toRows`; see `BackBooksRow.askedPeriods`. */
    readonly askedPeriods: StatementPeriod[];
    /** How many of `askedPeriods` are still outstanding, from live coverage. */
    readonly missingCount: number;
    /** Passed back out so the panel can hand down one stable handler for every row. */
    readonly rowKey: string;
    readonly onUploadClick: (rowKey: string) => void;
}

/**
 * One bank account owed past statements, as a row in the building-back-books panel.
 *
 * The month range is what this task asked for while the count is how much of it is still
 * outstanding — so "Jan – Apr 2026 (2 statements)" reads as progress against a fixed request
 * rather than the request itself shrinking under the member.
 *
 * The range is the *task's* ask, not the account's `required_periods`. The account's window
 * reaches back to the books start and is usually wider, and showing it here asked the member for
 * months this task was never about.
 */
export default function BuildingBackBooksAccountRow({
    account,
    state,
    askedPeriods,
    missingCount,
    rowKey,
    onUploadClick,
}: Readonly<BuildingBackBooksAccountRowProps>) {
    const theme = useTheme();

    const handleUploadClick = useCallback(() => onUploadClick(rowKey), [onUploadClick, rowKey]);

    // Coverage alone decides this. The child task's own status is deliberately not consulted: the
    // two drift — an operator can complete the task without statements arriving, and deleting a
    // statement puts a month back after completion — and honouring the task produced a row with a
    // success check beside an outstanding count and no way to supply the months still being asked
    // for. The task's status decides whether this row exists, not what it says.
    const showCheck = state === 'complete';

    // Never a blank line in any branch — a blank is indistinguishable from a broken row.
    const status: { label: string; color: string } = (() => {
        if (state === 'complete') {
            return { label: 'All statements uploaded', color: 'success.main' };
        }
        if (state === 'unknown') {
            return { label: 'Statement months not determined yet', color: 'text.secondary' };
        }
        const noun = missingCount === 1 ? 'statement' : 'statements';
        const range = formatMonthRangeShort(askedPeriods);
        return {
            label: range ? `${range} (${missingCount} ${noun})` : `${missingCount} ${noun} needed`,
            color: 'text.secondary',
        };
    })();

    return (
        <Stack direction="row" sx={{ alignItems: 'center', gap: 2, px: 2, py: 2 }}>
            <LogoFrame alignItems="center" justifyContent="center">
                <BankLogo institutionId={account.plaid_institution_id ?? ''} width={40} height={40} />
            </LogoFrame>
            <Stack sx={{ flex: 1, minWidth: 0, gap: 0.25 }}>
                <Typography variant="subtitle1" noWrap>
                    {accountDisplayName(account)}
                </Typography>
                {/* Mask alone. It is here to tell two accounts at one institution apart — which the
                    design omits and a member with two Chase accounts needs — and the account type
                    does not help with that, so it only adds a word to read. */}
                <Typography variant="body2" color="text.secondary">
                    {account.account_mask}
                </Typography>
                <Typography variant="body2" color={status.color}>
                    {status.label}
                </Typography>
            </Stack>
            <Stack direction="row" sx={{ alignItems: 'center', flexShrink: 0 }}>
                {showCheck && <CheckCircleFilledIcon fill={theme.palette.success.main} />}
                {!showCheck && (
                    <Button
                        variant="contained"
                        color="secondary"
                        size="small"
                        onClick={handleUploadClick}
                        sx={{ minWidth: 100 }}>
                        Upload
                    </Button>
                )}
            </Stack>
        </Stack>
    );
}
